import { useState, useEffect, useRef } from 'preact/hooks';
import { haptic } from '../utils/haptic';
import type { ProjectTag } from '@voice-assistant/shared';

interface Command {
  name: string;
  description: string;
  parameterized: boolean;
  native?: boolean;
}

const NATIVE_COMMANDS: Command[] = [
  { name: '/context', description: 'Show context window usage', parameterized: false, native: true },
  { name: '/compact', description: 'Compact conversation to free context', parameterized: false, native: true },
];

interface Props {
  onSendCommand: (text: string) => void;
  checkedKeys: string[];
  totalProjectCount: number;
  projectTags: ProjectTag[];
  checkedProjects: Set<string>;
  onToggleProject: (key: string) => void;
  commands: {name: string; description: string; parameterized?: boolean}[];
}

export function CommandPalette({ onSendCommand, checkedKeys, totalProjectCount, projectTags, checkedProjects, onToggleProject, commands: externalCommands }: Props) {
  const [expanded, setExpanded] = useState(false);
  const [projectsExpanded, setProjectsExpanded] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  const commands: Command[] = [...NATIVE_COMMANDS, ...externalCommands.map((c) => ({ ...c, parameterized: !!c.parameterized }))];

  useEffect(() => {
    if (!expanded && !projectsExpanded) return;
    const onPointerDown = (e: Event) => {
      const t = e.target as Node;
      if (rootRef.current?.contains(t)) return;
      setExpanded(false);
      setProjectsExpanded(false);
    };
    document.addEventListener('pointerdown', onPointerDown, true);
    return () => document.removeEventListener('pointerdown', onPointerDown, true);
  }, [expanded, projectsExpanded]);

  const handleCommand = (cmd: Command) => {
    let text = cmd.name;
    if (cmd.parameterized && checkedKeys.length > 0) {
      text += ' ' + checkedKeys.join(' ');
    }
    onSendCommand(text);
    setExpanded(false);
    haptic(30);
  };

  const isActive = (cmd: Command) => cmd.parameterized && checkedKeys.length > 0;

  return (
    <div ref={rootRef} class={`cmd-palette${expanded ? ' expanded' : ''}${projectsExpanded ? ' projects-expanded' : ''}`}>
      {(expanded || projectsExpanded) && (
        <div class="cmd-palette-sheets">
          {expanded && (
            <div class="cmd-palette-grid">
              {commands.map((cmd) => (
                <button
                  key={cmd.name}
                  class={`cmd-palette-btn${cmd.native ? ' native' : ''}${isActive(cmd) ? ' has-projects' : ''}`}
                  onClick={() => handleCommand(cmd)}
                  title={cmd.description + (isActive(cmd) ? ` (${checkedKeys.length >= totalProjectCount ? 'all' : checkedKeys.length + ' selected'})` : '')}
                >
                  {cmd.name}
                  {isActive(cmd) && (
                    <span class="cmd-palette-badge">
                      {checkedKeys.length >= totalProjectCount ? 'all' : checkedKeys.length}
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}
          {projectsExpanded && (
            <div class="cmd-palette-grid projects-grid">
              <div class="projects-grid-header">
                <span>
                  {checkedProjects.size > 0
                    ? `${checkedProjects.size} of ${projectTags.length} selected`
                    : `${projectTags.length} projects`}
                </span>
                {checkedProjects.size > 0 && (
                  <button
                    class="projects-grid-clear"
                    onClick={() => { for (const k of [...checkedProjects]) onToggleProject(k); }}
                  >
                    Clear
                  </button>
                )}
              </div>
              {projectTags.map((tag) => {
                const checked = checkedProjects.has(tag.key);
                return (
                  <button
                    key={tag.key}
                    class={`cmd-palette-btn project-item${checked ? ' checked' : ''}`}
                    onClick={() => { onToggleProject(tag.key); haptic(15); }}
                  >
                    <span class="project-check">{checked ? '\u2713' : ''}</span>
                    {tag.label}
                    {tag.url && (
                      <a
                        class="project-ext-link"
                        href={tag.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {'\u2197'}
                      </a>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}
      <div class="cmd-palette-bar">
        <button
          type="button"
          class="cmd-palette-toggle"
          onClick={() => { setExpanded(!expanded); if (projectsExpanded) setProjectsExpanded(false); haptic(20); }}
          title={expanded ? 'Close commands' : 'Commands (/)'}
        >
          {expanded ? '\u2715' : '/'}
        </button>
        <button
          type="button"
          class={`cmd-palette-projects${projectsExpanded ? ' active' : ''}`}
          onClick={() => { setProjectsExpanded(!projectsExpanded); if (expanded) setExpanded(false); haptic(20); }}
          title={projectsExpanded ? 'Close projects' : 'Projects'}
        >
          projects{checkedProjects.size > 0 ? ` (${checkedProjects.size})` : ''}
        </button>
      </div>
    </div>
  );
}
